import { z } from "zod";
import { CustomFieldValuesSchema } from "./customFields";
import { CurrencyCodeSchema } from "./currency";
import { ItemUnitSchema } from "./items";

const DecimalStringSchema = z
  .string()
  .max(100)
  .regex(/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/);

/** Stock record input with cost in the organization’s base currency and canonical item units. */
export const InventoryLineInputSchema = z
  .object({
    itemId: z.string().min(1),
    locationId: z.string().min(1),
    availableQty: DecimalStringSchema.nullable(),
    quantityUnit: ItemUnitSchema,
    unitCost: DecimalStringSchema.nullable(),
    costUnit: ItemUnitSchema.nullable(),
    metadata: CustomFieldValuesSchema.default({}),
  })
  .superRefine((line, ctx) => {
    if (line.unitCost !== null && !line.costUnit) {
      ctx.addIssue({
        code: "custom",
        message: "Inventory cost requires a cost unit",
        path: ["unitCost"],
      });
    }
  });
/** Validated stock input for an inventory line write. */
export type InventoryLineInput = z.infer<typeof InventoryLineInputSchema>;

/** Read-only stock record, scoped to an organization. */
export const InventoryLineSchema = InventoryLineInputSchema.safeExtend({
  id: z.string(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
/** Stored inventory line. */
export type InventoryLine = z.infer<typeof InventoryLineSchema>;

/** Bounded stock lookup filters. */
export const InventoryFilterSchema = z.object({
  itemId: z.string().min(1).optional(),
  itemIds: z.array(z.string().min(1)).max(100).optional(),
  search: z.string().optional(),
  locationId: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce
    .number()
    .int()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER)
    .default(0),
});
/** Validated inventory query parameters. */
export type InventoryFilter = z.infer<typeof InventoryFilterSchema>;

/** Inventory record with names resolved through the owning services. */
export const HydratedInventoryLineSchema = InventoryLineSchema.safeExtend({
  currency: CurrencyCodeSchema,
  itemName: z.string().nullable(),
  locationName: z.string().nullable(),
});
/** Read-only inventory row displayed in Items. */
export type HydratedInventoryLine = z.infer<typeof HydratedInventoryLineSchema>;
/** Paginated inventory response. */
export const ListInventoryLinesResponseSchema = z.object({
  lines: z.array(HydratedInventoryLineSchema),
  total: z.number().int().nonnegative(),
});
/** Organization inventory presence, independent of list filters. */
export const InventoryStatusResponseSchema = z.object({
  hasInventory: z.boolean(),
});
